/**
 * FairwayBid – was der Nutzer im Posteingang schon gesehen hat
 *
 * Der rote Punkt am Posteingang zählt keine "ungelesenen Nachrichten": So
 * etwas kennt das Transaktionsmodell nicht. Gezählt werden Vorgänge, die auf
 * eine Reaktion warten – für eine Golfanlage die eingegangenen Angebote, für
 * einen Händler die Entscheidungen über seine eigenen.
 *
 * Diese Zahl verschwindet von selbst, sobald der Vorgang weitergeht. Das ist
 * richtig, aber es ist nicht das, was man von einem Posteingang erwartet: Wer
 * hineingesehen hat, will den Punkt los sein, auch wenn er noch nicht
 * entschieden hat.
 *
 * Deshalb merken wir uns, welche Vorgänge schon einmal im Posteingang standen.
 * Der Punkt zeigt dann nur noch, was seitdem dazugekommen ist.
 *
 * Bewusst im Browser und nicht am Nutzerkonto:
 *
 *   - "Gesehen" ist eine Eigenschaft dieses Geräts, nicht des Kontos. Wer am
 *     Telefon hineinsieht, hat es am Schreibtisch noch nicht gesehen - und
 *     das ist eher hilfreich als störend.
 *   - Es kostet keinen Schreibzugriff auf erweiterte Daten und keine
 *     Integration-API.
 *   - Geht es verloren (privates Fenster, geleerter Speicher), erscheint der
 *     Punkt wieder. Das ist der harmlose Fehlerfall: einmal zu viel erinnert.
 *
 * Der Speicher wird bei jedem Merken auf die aktuell offenen Vorgänge
 * eingedampft, damit er nicht über Jahre wächst.
 */

const SCHLUESSEL = 'fwb.posteingang.gesehen';

const speicher = () => {
  // Serverseitig gibt es keinen localStorage, und ein privates Fenster kann
  // den Zugriff verweigern. Beides darf die Seite nicht mitreissen.
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

/** Die gemerkten IDs. Immer ein Array, auch wenn nichts gespeichert ist. */
export const holeGesehen = () => {
  const s = speicher();
  if (!s) return [];
  try {
    const roh = s.getItem(SCHLUESSEL);
    const werte = roh ? JSON.parse(roh) : [];
    return Array.isArray(werte) ? werte.filter(id => typeof id === 'string') : [];
  } catch (e) {
    return [];
  }
};

/**
 * Merkt Vorgänge als gesehen.
 *
 * @param {Array<string>} ids die jetzt sichtbaren Vorgänge
 * @param {Array<string>} bisher was schon gemerkt war
 * @returns {Array<string>} der neue Stand - auch dann, wenn das Speichern
 *          fehlschlägt: Innerhalb der Sitzung soll der Punkt trotzdem gehen.
 */
export const merkeGesehen = (ids = [], bisher = [], nochOffen = null) => {
  const gueltig = (Array.isArray(ids) ? ids : []).filter(Boolean);
  const alt = (Array.isArray(bisher) ? bisher : []).filter(Boolean);

  // Eingedampft auf das, was gerade offen ist: Ein Vorgang, der aus der Liste
  // verschwunden ist, muss nicht bis in alle Zeit als gesehen gelten.
  //
  // Wonach eingedampft wird, ist wichtig: Der Posteingang zeigt immer nur
  // einen Reiter. Wuerde nach den dort sichtbaren Vorgaengen eingedampft,
  // vergaesse ein Blick in "Meine Anfragen" alles, was unter "Meine
  // Angebote" schon gesehen war - und der Punkt kaeme zurueck.
  const behalten = Array.isArray(nochOffen) ? new Set(nochOffen.filter(Boolean)) : new Set(gueltig);
  const neu = [...new Set([...alt.filter(id => behalten.has(id)), ...gueltig])];

  const s = speicher();
  if (s) {
    try {
      s.setItem(SCHLUESSEL, JSON.stringify(neu));
    } catch (e) {
      // Voller oder gesperrter Speicher: Der Punkt kommt beim naechsten
      // Seitenaufbau wieder. Mehr passiert nicht.
    }
  }
  return neu;
};

/** Wie viele der offenen Vorgänge noch niemand gesehen hat. */
export const ungeleseneAnzahl = (ids = [], gesehen = []) => {
  const bekannt = new Set((Array.isArray(gesehen) ? gesehen : []).filter(Boolean));
  return (Array.isArray(ids) ? ids : []).filter(id => id && !bekannt.has(id)).length;
};

/** Beim Abmelden: Der naechste Nutzer an diesem Browser faengt bei null an. */
export const vergissGesehen = () => {
  const s = speicher();
  if (!s) return;
  try {
    s.removeItem(SCHLUESSEL);
  } catch (e) {
    // Nichts zu tun.
  }
};
